import { z } from 'zod';
import { zodToJsonSchema } from 'zod-to-json-schema';
import { RunnableToolFunctionWithParse } from 'openai/lib/RunnableFunction';
import { JSONSchema } from 'openai/lib/jsonschema';

const indent = (n: number) => '  '.repeat(n);

/**
 * Parse error from various types
 */
export const parseError = (error: Error | string | object) => {
	if (error instanceof Error) {
		try {
			return JSON.parse(error.message);
		} catch {
			return error.message;
		}
	}
	if (typeof error === 'string') {
		return error;
	}
	return error;
};

/**
 * Check if a schema or its descendants contain any properties from the keep list.
 * Used to determine if a container (object/array) should be kept.
 */
const hasKeptProperties = (
	schema: z.ZodTypeAny,
	propertiesToKeep: string[],
): boolean => {
	// Unwrap optionals and nullables
	let unwrapped = schema;
	let typeName = unwrapped?._def?.typeName;
	while (typeName === 'ZodOptional' || typeName === 'ZodNullable') {
		unwrapped = (
			unwrapped as z.ZodOptional<any> | z.ZodNullable<any>
		).unwrap();
		typeName = unwrapped?._def?.typeName;
	}

	// Check arrays - recurse into element type
	if (typeName === 'ZodArray') {
		return hasKeptProperties(
			(unwrapped as z.ZodArray<any>).element,
			propertiesToKeep,
		);
	}

	// Check unions - any option has kept properties
	if (typeName === 'ZodUnion') {
		return (unwrapped as z.ZodUnion<any>).options.some(
			(opt: z.ZodTypeAny) => hasKeptProperties(opt, propertiesToKeep),
		);
	}

	// Check objects - any property name matches or descendants match
	if (typeName === 'ZodObject') {
		for (const [key, value] of Object.entries(
			(unwrapped as z.ZodObject<any>).shape,
		)) {
			// If property name is in keep list, we found one
			if (propertiesToKeep.includes(key)) {
				return true;
			}
			// Otherwise check if this property's descendants contain kept properties
			if (hasKeptProperties(value as z.ZodTypeAny, propertiesToKeep)) {
				return true;
			}
		}
	}

	return false;
};

/**
 * Internal generic function to process schema properties with include or exclude mode.
 * This is the shared implementation for both filterSchemaProperties and excludeSchemaProperties.
 *
 * @param schema - The Zod schema to process
 * @param propertyNames - Array of property names to include or exclude
 * @param mode - 'include' to keep only specified properties, 'exclude' to remove them
 * @returns A new processed Zod schema
 */
const processSchemaProperties = (
	schema: z.ZodTypeAny,
	propertyNames: string[],
	mode: 'include' | 'exclude',
): z.ZodTypeAny => {
	const typeName = schema?._def?.typeName;

	// Handle optionals - recurse and rewrap
	if (typeName === 'ZodOptional') {
		return z.ZodOptional.create(
			processSchemaProperties(
				(schema as z.ZodOptional<any>).unwrap(),
				propertyNames,
				mode,
			),
		);
	}

	// Handle nullables - recurse and rewrap
	if (typeName === 'ZodNullable') {
		return z.ZodNullable.create(
			processSchemaProperties(
				(schema as z.ZodNullable<any>).unwrap(),
				propertyNames,
				mode,
			),
		);
	}

	// Handle arrays - recurse into element type
	if (typeName === 'ZodArray') {
		const processedElement = processSchemaProperties(
			(schema as z.ZodArray<any>).element,
			propertyNames,
			mode,
		);
		return z.ZodArray.create(processedElement, schema._def);
	}

	// Handle unions - recurse into each option
	if (typeName === 'ZodUnion') {
		const processedOptions = (schema as z.ZodUnion<any>).options.map(
			(opt: z.ZodTypeAny) =>
				processSchemaProperties(opt, propertyNames, mode),
		);
		return z.ZodUnion.create(processedOptions);
	}

	// Handle objects - mode-specific filtering
	if (typeName === 'ZodObject') {
		const filteredEntries: [string, z.ZodTypeAny][] = [];

		for (const [key, value] of Object.entries(
			(schema as z.ZodObject<any>).shape,
		)) {
			if (mode === 'include') {
				// INCLUDE mode: keep property if in list OR if it leads to properties in list
				if (propertyNames.includes(key)) {
					// Keep property entirely with all descendants
					filteredEntries.push([key, value as z.ZodTypeAny]);
				} else if (
					hasKeptProperties(value as z.ZodTypeAny, propertyNames)
				) {
					// Property leads to kept properties - keep and recurse
					const processedValue = processSchemaProperties(
						value as z.ZodTypeAny,
						propertyNames,
						mode,
					);
					filteredEntries.push([key, processedValue]);
				}
				// Otherwise, property is removed (not added to filteredEntries)
			} else {
				// EXCLUDE mode: remove property if in list, otherwise keep and recurse
				if (propertyNames.includes(key)) {
					// Property is in exclude list - remove entirely (don't add to filteredEntries)
				} else {
					// Property not in exclude list - keep and recurse to handle nested exclusions
					const processedValue = processSchemaProperties(
						value as z.ZodTypeAny,
						propertyNames,
						mode,
					);
					filteredEntries.push([key, processedValue]);
				}
			}
		}

		const filteredShape = Object.fromEntries(
			filteredEntries,
		) as z.ZodRawShape;
		return z.ZodObject.create(filteredShape, schema._def);
	}

	// For all other types (primitives, enums, literals, etc.), return as-is
	return schema;
};

/**
 * Filter Zod schema properties using path-based filtering.
 * Keeps properties in the keep list, all their descendants, and the path to reach them.
 *
 * Algorithm:
 * - If property name is in keep list → keep it entirely (with all descendants)
 * - If property is a container → check if it contains kept properties
 *   - If yes → keep container and recurse
 *   - If no → remove it
 *
 * @param schema - The Zod schema to filter
 * @param propertiesToKeep - Array of property names to keep (with all their descendants)
 * @returns A new filtered Zod schema
 */
export const filterSchemaProperties = (
	schema: z.ZodTypeAny,
	propertiesToKeep: string[],
): z.ZodTypeAny => {
	return processSchemaProperties(schema, propertiesToKeep, 'include');
};

/**
 * Exclude specified properties from a Zod schema.
 * Removes properties in the exclude list and all their descendants.
 *
 * Algorithm:
 * - If property name is in exclude list → remove entirely (with all descendants)
 * - Otherwise → keep and recurse to handle nested exclusions
 *
 * @param schema - The Zod schema to process
 * @param propertiesToExclude - Array of property names to remove (with all their descendants)
 * @returns A new filtered Zod schema
 */
export const excludeSchemaProperties = (
	schema: z.ZodTypeAny,
	propertiesToExclude: string[],
): z.ZodTypeAny => {
	return processSchemaProperties(schema, propertiesToExclude, 'exclude');
};

/**
 * Convert a Zod schema to a readable type string representation.
 * Uses _def.typeName instead of instanceof because instanceof is unreliable
 * for classes extending built-ins (like Error, Array) when code is downleveled to ES5.
 * See: https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-2.html#support-for-newtarget
 */
export const zodToReadableType = (
	schema: z.ZodTypeAny,
	depth = 0,
	propName?: string,
	isOptional?: boolean,
): string => {
	const typeName = schema?._def?.typeName;

	// Optionals
	if (typeName === 'ZodOptional') {
		return zodToReadableType(
			(schema as z.ZodOptional<any>).unwrap(),
			depth,
			propName,
			true,
		);
	}
	// Nullables
	if (typeName === 'ZodNullable') {
		return (
			zodToReadableType(
				(schema as z.ZodNullable<any>).unwrap(),
				depth,
				propName,
				isOptional,
			) + ' | null'
		);
	}
	// Array
	if (typeName === 'ZodArray') {
		const inner = zodToReadableType(
			(schema as z.ZodArray<any>).element,
			depth,
		);
		return `Array<${inner}>${
			schema.description ? ` - ${schema.description}` : ''
		}`;
	}
	// Object: print each property on its own line, with descriptions
	if (typeName === 'ZodObject') {
		const props = Object.entries((schema as z.ZodObject<any>).shape)
			.map(
				([k, v]: [string, any]) =>
					`${indent(depth + 1)}${k}${
						v.isOptional?.() ? '?' : ''
					}: ${zodToReadableType(v, depth + 1, k, v.isOptional?.())}${
						v.description ? ` - ${v.description}` : ''
					}`,
			)
			.join(',\n');
		return (
			`{\n${props}\n${indent(depth)}}` +
			(schema.description ? ` - ${schema.description}` : '')
		);
	}
	// Enum
	if (typeName === 'ZodEnum') {
		const opts = (schema as z.ZodEnum<any>).options
			.map((o: string) => `"${o}"`)
			.join(' | ');
		return opts + (schema.description ? ` - ${schema.description}` : '');
	}
	// Union
	if (typeName === 'ZodUnion') {
		return (schema as z.ZodUnion<any>).options
			.map((opt: z.ZodTypeAny) => zodToReadableType(opt, depth))
			.join(' | ');
	}
	// Literal
	if (typeName === 'ZodLiteral') {
		return JSON.stringify((schema as z.ZodLiteral<any>).value);
	}
	// String, Number, Boolean
	if (typeName === 'ZodString') return 'string';
	if (typeName === 'ZodNumber') return 'number';
	if (typeName === 'ZodBoolean') return 'boolean';
	// Fallback
	return 'any';
};

/**
 * Create a decoupled zodFunction that wraps Zod schemas for OpenAI tools.
 *
 * This is a pure function that does NOT depend on stores or session management.
 * It only wraps the schema and delegates to the provided function with a generic context.
 *
 * @param config - Configuration for the tool
 * @param config.schema - The Zod schema for parameters
 * @param config.name - The function name
 * @param config.description - The function description
 * @param config.function - The function to execute (receives args and generic context)
 * @param config.strict - Whether to use strict mode
 * @returns A runnable tool function for OpenAI
 */
export function zodFunction<T extends object, TContext = any>({
	function: fn,
	schema,
	description = '',
	name,
	strict = false,
}: {
	function: (args: T, context: TContext) => Promise<object>;
	schema: z.ZodSchema<T>;
	description?: string;
	name?: string;
	strict?: boolean;
}): RunnableToolFunctionWithParse<T> {
	return {
		type: 'function',
		function: {
			function: async (args: T, context: any) => {
				return fn(args, context as TContext);
			},
			name: name ?? (fn as any).name,
			description: description,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			parameters: zodToJsonSchema(schema as any) as JSONSchema,
			strict,
			parse(input: string): T {
				const obj = JSON.parse(input);
				return schema.parse(obj);
			},
		},
	};
}
